import React, { useState } from 'react'
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  Flex,
  Spinner,
  Select,
  HStack,
  FormControl,
  FormLabel,
  Badge,
  Tooltip,
  useColorModeValue,
  Icon,
  Divider,
  Button,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Image,
  useDisclosure,
  Link,
} from '@chakra-ui/react'
import { format, isValid, parseISO, isPast } from 'date-fns'
import { Pagination } from '../../../../components/Pagination'
import {
  PhoneIcon,
  EmailIcon,
  InfoIcon,
  CalendarIcon,
  TimeIcon,
  ChatIcon,
  ViewIcon,
  ExternalLinkIcon,
  CheckCircleIcon,
  WarningIcon,TriangleDownIcon, TriangleUpIcon,
} from '@chakra-ui/icons'
import { FaWhatsapp, FaFileAlt } from 'react-icons/fa'

export const AgendamentosList = ({ agendamentos, isLoading, paginacao, onChangePaginacao, onChangeLimite, isError,filtros, toggleOrdenacao,}) => {
  // Estado para controlar qual mensagem está sendo visualizada
  const [selectedMessage, setSelectedMessage] = useState(null)
  const { isOpen, onOpen, onClose } = useDisclosure()
  const cancelRef = React.useRef()

  const renderSortIcon = (filtros, campo) => {
  if (!filtros.filtrar_por) {
    return <Icon as={TriangleDownIcon} color="gray.300" boxSize={3} />
  }

  if (filtros.filtrar_por !== campo) {
    return null
  }
  return filtros.ordenacao === 'asc' ? (
    <Icon as={TriangleUpIcon} color="gray.700" boxSize={3} />
  ) : (
    <Icon as={TriangleDownIcon} color="gray.700" boxSize={3} />
  )
}

  // Opções para limite por página
  const opcoesLimite = [10, 25, 50, 100]
  const bgColor = useColorModeValue('white', 'gray.800')
  const borderColor = useColorModeValue('gray.200', 'gray.700')
  const headerBg = useColorModeValue('gray.50', 'gray.700')

  const handleLimiteChange = (e) => {
    const novoLimite = parseInt(e.target.value)
    onChangeLimite(novoLimite)
  }

  const formatarData = (dataString) => {
    if (!dataString) return '-'

    try {
      const data = parseISO(dataString)
      if (!isValid(data)) return '-'

      const dataFormatada = format(data, 'dd/MM/yyyy')
      const horaFormatada = format(data, 'HH:mm')

      return (
        <Box lineHeight="1.2">
          <Text>{dataFormatada}</Text>
         <Text fontSize="1em">{horaFormatada}</Text>
        </Box>
      )
    } catch (error) {
      return '-'
    }
  }

  const getContatoInfo = (agendamento) => {
    if (agendamento.telefone) {
      return (
        <HStack spacing={1}>
          <PhoneIcon boxSize={3} color="blue.500" />
          <Text fontSize="sm">{agendamento.telefone}</Text>
        </HStack>
      )
    } else if (agendamento.fax) {
      return (
        <HStack spacing={1}>
          <PhoneIcon boxSize={3} color="teal.500" />
          <Text fontSize="sm">{agendamento.fax}</Text>
        </HStack>
      )
    } else if (agendamento.email) {
      return (
        <HStack spacing={1}>
          <EmailIcon boxSize={3} color="green.500" />
          <Text fontSize="sm">{agendamento.email}</Text>
        </HStack>
      )
    }
    return '-'
  }

  // Função para truncar texto e mostrar apenas os primeiros caracteres
  const truncateText = (text, maxLength = 10) => {
    if (!text) return '-'
    if (text.length <= maxLength) return text
    return `${text.substring(0, maxLength)}...`
  }

  // Função para abrir o diálogo com a mensagem escolhida
  const handleViewMessage = (agendamento) => {
    setSelectedMessage(agendamento)
    onOpen()
  }

  // Função para obter ícone e cor para o tipo de agendamento
  const getTipoAgendamentoInfo = (agendamento) => {
    const tipo = agendamento.tipo_agendamento?.toLowerCase() || ''

    if (tipo === 'email') {
      return (
        <Badge colorScheme="blue" variant="subtle" px={2} py={1} borderRadius="md">
          <HStack spacing={1}>
            <EmailIcon boxSize={3} />
            <Text fontSize="xs">Email</Text>
          </HStack>
        </Badge>
      )
    } else if (tipo === 'whatsapp') {
      // Se for WhatsApp e tiver um link, torna o ícone clicável
      return (
        <Badge colorScheme="green" variant="subtle" px={2} py={1} borderRadius="md">
          <HStack spacing={1}>
            {agendamento.link_whatsapp ? (
              <Link href={agendamento.link_whatsapp} isExternal _hover={{ color: 'green.600' }} display="flex" alignItems="center">
                <Icon as={FaWhatsapp} boxSize={3} />
                <Text fontSize="xs" ml={1}>
                  WhatsApp
                </Text>
                <ExternalLinkIcon mx="2px" boxSize={2} />
              </Link>
            ) : (
              <>
                <Icon as={FaWhatsapp} boxSize={3} />
                <Text fontSize="xs">WhatsApp</Text>
              </>
            )}
          </HStack>
        </Badge>
      )
    } else if (tipo === 'telefone') {
      return (
        <Badge colorScheme="orange" variant="subtle" px={2} py={1} borderRadius="md">
          <HStack spacing={1}>
            <PhoneIcon boxSize={3} />
            <Text fontSize="xs">Telefone</Text>
          </HStack>
        </Badge>
      )
    }

    return (
      <Text fontSize="sm" color="gray.500">
        -
      </Text>
    )
  }

  // Função para determinar o status do agendamento
  const getStatusAgendamento = (agendamento) => {
    if (!agendamento.data_agendamento) return '-'

    try {
      const dataAgendamento = parseISO(agendamento.data_agendamento)

      if (!isValid(dataAgendamento)) return '-'

      // Se a data de agendamento for no passado, consideramos como enviado
      if (isPast(dataAgendamento)) {
        return (
          <Badge colorScheme="green" variant="solid" px={2} py={1} borderRadius="full">
            <HStack spacing={1}>
              <CheckCircleIcon boxSize={3} />
              <Text fontSize="xs">Enviado</Text>
            </HStack>
          </Badge>
        )
      } else {
        return (
          <Badge colorScheme="yellow" variant="solid" px={2} py={1} borderRadius="full">
            <HStack spacing={1}>
              <WarningIcon boxSize={3} />
              <Text fontSize="xs">Pendente</Text>
            </HStack>
          </Badge>
        )
      }
    } catch (error) {
      return '-'
    }
  }

  // Função para criar link para a proposta
  const getPropostaLink = (agendamento) => {
    if (!agendamento.cod_inno) return '-'

    // Caminho para a página de proposta
    const propostaUrl = `/vendas/orcamento-pedido/${agendamento.cod_inno}`

    return (
      <Link
        href={propostaUrl}
        isExternal={false}
        color="blue.600"
        fontWeight="medium"
        display="flex"
        alignItems="center"
        _hover={{ textDecoration: 'underline', color: 'blue.700' }}
      >
        <Icon as={FaFileAlt} mr={1} color="blue.500" boxSize={3} />
        {agendamento.cod_inno}
        <ExternalLinkIcon ml={1} boxSize={3} />
      </Link>
    )
  }
  

  return (
    <Box bg={bgColor} p={5} borderRadius="md" shadow="sm" borderWidth="1px" borderColor={borderColor}>
      {isLoading ? (
        <Flex justify="center" align="center" height="300px">
          <Spinner size="xl" color="blue.500" thickness="4px" />
        </Flex>
      ) : isError ? (
        <Flex justify="center" align="center" height="300px" direction="column" bg="red.50" borderRadius="md" p={8}>
          <Icon as={InfoIcon} boxSize={12} color="red.400" mb={4} />
          <Text fontSize="lg" fontWeight="medium" color="red.600">
            Erro ao carregar dados
          </Text>
          <Text color="red.500" textAlign="center" mt={2}>
            Ocorreu um erro ao buscar os agendamentos. Por favor, tente novamente.
          </Text>
        </Flex>
      ) : agendamentos.length === 0 ? (
        <Flex justify="center" align="center" height="300px" direction="column" bg="gray.50" borderRadius="md" p={8}>
          <Icon as={InfoIcon} boxSize={12} color="blue.400" mb={4} />
          <Text fontSize="lg" fontWeight="medium" color="gray.600">
            Nenhum agendamento encontrado
          </Text>
          <Text color="gray.500" textAlign="center" mt={2}>
            Não foram encontrados agendamentos de mensagens com os filtros atuais.
          </Text>
        </Flex>
      ) : (
        <>
          <Box>
            <Table variant="simple"size="sm"
              sx={{
                'th, td': {
                  px: '8px',
                  py: '8px',
                },
              }}
            >
              <Thead bg={headerBg}>
                <Tr>
                  <Th width="120px">Proposta</Th>
                  <Th width="120px">Cód. Parceiro</Th>
                  <Th width="180px">Nome do Contato</Th>
                  <Th width="150px">Contato</Th>
                  <Th width="120px">Tipo</Th>
                  <Th width="120px">
                    <Button size="sm" variant="ghost" px={-6} 
                      onClick={() => toggleOrdenacao('contato')}
                      rightIcon={renderSortIcon(filtros, 'contato')}
                    >
                      <Text whiteSpace="pre-line" textAlign="start" fontSize="12px" fontWeight="bold">
                        DATA{'\n'}CONTATO
                      </Text>
                    </Button>
                  </Th>
                  <Th width="120px">
                    <Button size="sm" variant="ghost" px={-4} 
                      onClick={() => toggleOrdenacao('agendamento')}
                      rightIcon={renderSortIcon(filtros, 'agendamento')}
                    >
                      <Text whiteSpace="pre-line" textAlign="start" fontSize="12px" fontWeight="bold">
                        DATA{'\n'}AGENDAMENTO
                      </Text>
                    </Button>
                  </Th>
                  <Th width="100px">Status</Th>
                  <Th width="100px">Vendedor</Th>
                  <Th width="150px">Observação</Th>
                  <Th width="150px">Mensagem Escolhida</Th>
                </Tr>
              </Thead>
              <Tbody>
                {agendamentos.map((agendamento, index) => (
                  <Tr key={`${agendamento.cod_inno}-${index}`} _hover={{ bg: 'gray.50' }}>
                    <Td>{getPropostaLink(agendamento)}</Td>
                    <Td>{agendamento.cod_parc || '-'}</Td>
                    <Td>
                      <Tooltip label={agendamento.nome_contato} placement="top" isDisabled={!agendamento.nome_contato}>
                        <Text isTruncated maxW="180px">
                          {agendamento.nome_contato || '-'}
                        </Text>
                      </Tooltip>
                    </Td>
                    <Td>{getContatoInfo(agendamento)}</Td>
                    <Td>{getTipoAgendamentoInfo(agendamento)}</Td>
                    <Td>{formatarData(agendamento.data_contato)}</Td>
                    <Td>
                      <HStack>
                        <CalendarIcon color="blue.500" boxSize={3} />
                        <Text fontSize="sm">{formatarData(agendamento.data_agendamento)}</Text>
                      </HStack>
                    </Td>
                    <Td>{getStatusAgendamento(agendamento)}</Td>
                    <Td>
                      <Badge colorScheme="purple" px={2} py={1} borderRadius="full">
                        {agendamento.vendedor || (agendamento.cod_vend ? `ID: ${agendamento.cod_vend}` : '-')}
                      </Badge>
                    </Td>
                    <Td>
                      {agendamento.observacao ? (
                        <Tooltip
                          label={agendamento.observacao}
                          placement="top"
                          hasArrow
                          bg="gray.700"
                          color="white"
                          maxW="400px"
                          p={2}
                          borderRadius="md"
                        >
                          <HStack spacing={1}>
                            <ChatIcon color="teal.500" boxSize={3} />
                            <Text fontSize="sm" fontStyle="italic">
                              {truncateText(agendamento.observacao, 10)}
                            </Text>
                          </HStack>
                        </Tooltip>
                      ) : (
                        '-'
                      )}
                    </Td>
                    <Td>
                      {agendamento.mensagem_escolhida ? (
                        <Button size="xs" leftIcon={<ViewIcon />} colorScheme="blue" variant="outline" onClick={() => handleViewMessage(agendamento)}>
                          Visualizar
                        </Button>
                      ) : (
                        <Text fontSize="sm" color="gray.500">
                          Não definida
                        </Text>
                      )}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>

          {/* AlertDialog para mostrar a mensagem escolhida */}
          <AlertDialog isOpen={isOpen} leastDestructiveRef={cancelRef} onClose={onClose} size="lg" isCentered>
            <AlertDialogOverlay>
              <AlertDialogContent>
                <AlertDialogHeader fontSize="lg" fontWeight="bold">
                  Mensagem Escolhida
                </AlertDialogHeader>

                <AlertDialogBody>
                  {selectedMessage?.mensagem_escolhida ? (
                    <Box>
                      <Text mb={2} fontWeight="medium">
                        Código: {selectedMessage.cod_mensagem || 'N/A'}
                      </Text>
                      <Text mb={2} fontSize="sm" color="gray.600">
                        Cliente: {selectedMessage.nome_contato || 'N/A'}
                        {selectedMessage.cod_parc ? ` (${selectedMessage.cod_parc})` : ''}
                      </Text>
                      <Box borderWidth="1px" borderRadius="md" p={3} bg="gray.50">
                        {/* Exibimos o texto da mensagem com formatação de quebra de linha preservada */}
                        <Text fontStyle="italic" whiteSpace="pre-wrap">
                          {selectedMessage.mensagem_escolhida || 'Conteúdo da mensagem não disponível'}
                        </Text>
                      </Box>
                    </Box>
                  ) : (
                    <Text>Nenhuma mensagem definida para este agendamento.</Text>
                  )}
                </AlertDialogBody>

                <AlertDialogFooter>
                  <Button ref={cancelRef} onClick={onClose}>
                    Fechar
                  </Button>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialogOverlay>
          </AlertDialog>

          <Divider my={4} />

          <Flex justify="space-between" align="center" flexWrap="wrap" gap={2}>
            <Text fontSize="sm" color="gray.600">
              Total:{' '}
              <Text as="span" fontWeight="bold">
                {paginacao.total || 0}
              </Text>{' '}
              agendamentos
            </Text>

            <HStack spacing={4}>
              <FormControl display="flex" alignItems="center" w="auto">
                <FormLabel htmlFor="itensPorPagina" mb="0" fontSize="sm" whiteSpace="nowrap">
                  Itens por página:
                </FormLabel>
                <Select
                  id="itensPorPagina"
                  size="sm"
                  w="70px"
                  value={paginacao.por_pagina || paginacao.limite}
                  onChange={handleLimiteChange}
                  isDisabled={isLoading}
                  borderRadius="md"
                >
                  {opcoesLimite.map((opcao) => (
                    <option key={opcao} value={opcao}>
                      {opcao}
                    </option>
                  ))}
                </Select>
              </FormControl>

              <Pagination
                page={paginacao.pagina_atual || paginacao.pagina}
                setPage={onChangePaginacao}
                limit={paginacao.por_pagina || paginacao.limite}
                totalPages={paginacao.ultima_pagina || 1}
                isDisabled={isLoading}
              />
            </HStack>
          </Flex>
        </>
      )}
    </Box>
  )
}